import {
  tablesDB,
  DATABASE_ID,
  USERS_TABLE_ID,
  PRODUCTS_TABLE_ID,
  ORDERS_TABLE_ID,
  PAYMENT_METHOD_TABLE_ID,
  REQUESTS_TABLE_ID,
  CONVERSATIONS_TABLE_ID,
  MESSAGES_TABLE_ID,
  Query,
} from '../lib/appwrite';
import { getStore } from './adminStoreService';

export async function getStoreOverview(storeId) {
  return getStore(storeId);
}

export async function getStoreProducts(storeId) {
  const res = await tablesDB.listRows(DATABASE_ID, PRODUCTS_TABLE_ID, [
    Query.equal('storeId', storeId),
    Query.limit(100),
  ]);
  return res.rows || [];
}

export async function getStoreOrders(storeId, limit = 50) {
  const res = await tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [
    Query.equal('storeId', storeId),
    Query.orderDesc('orderDate'),
    Query.limit(limit),
  ]);
  return res.rows || [];
}

export async function getStorePaymentMethods(storeId) {
  const res = await tablesDB.listRows(DATABASE_ID, PAYMENT_METHOD_TABLE_ID, [
    Query.equal('storeId', storeId),
  ]);
  return res.rows || [];
}

export async function getStoreDeliveryRequests(storeId, limit = 50) {
  const res = await tablesDB.listRows(DATABASE_ID, REQUESTS_TABLE_ID, [
    Query.equal('storeId', storeId),
    Query.orderDesc('$createdAt'),
    Query.limit(limit),
  ]);
  return res.rows || [];
}

export async function getStoreCustomers(storeId) {
  const ordersRes = await tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [
    Query.equal('storeId', storeId),
  ]);
  const orders = ordersRes.rows || [];
  const customerIds = [...new Set(orders.map((o) => o.customerId).filter(Boolean))];
  const customers = [];
  for (const customerId of customerIds) {
    try {
      const user = await tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, customerId);
      if (user) {
        customers.push({
          ...user,
          orderCount: orders.filter((o) => o.customerId === customerId).length,
        });
      }
    } catch {
      customers.push({
        $id: customerId,
        uid: customerId,
        fname: 'Unknown',
        lname: '',
        email: null,
        phoneNumber: null,
        orderCount: orders.filter((o) => o.customerId === customerId).length,
      });
    }
  }
  const byOrder = {};
  orders.forEach((o) => {
    const key = o.orderId || o.$id;
    if (!byOrder[key]) byOrder[key] = { customerId: o.customerId, total: 0 };
    byOrder[key].total += parseFloat(o.totalAmount) || 0;
  });
  const customerStats = {};
  Object.values(byOrder).forEach(({ customerId, total }) => {
    if (!customerId) return;
    if (!customerStats[customerId]) customerStats[customerId] = { orderCount: 0, totalSpent: 0 };
    customerStats[customerId].orderCount += 1;
    customerStats[customerId].totalSpent += total;
  });
  customers.forEach((c) => {
    const s = customerStats[c.$id || c.uid] || { orderCount: 0, totalSpent: 0 };
    c.orderCount = s.orderCount;
    c.totalSpent = s.totalSpent;
  });
  customers.sort((a, b) => (b.totalSpent || b.orderCount || 0) - (a.totalSpent || a.orderCount || 0));
  return customers;
}

/**
 * Get detailed store analytics from orders: top customers, top products sold, delivery usage, etc.
 */
export async function getStoreAnalyticsDetail(storeId, ordersOverride) {
  let orders = ordersOverride;
  if (!orders?.length) {
    const res = await tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [
      Query.equal('storeId', storeId),
      Query.limit(5000),
    ]);
    orders = res.rows || [];
  }

  const byOrderId = {};
  const byCustomer = {};
  const byProduct = {};
  const byDelivery = {};
  const byStatus = {};

  orders.forEach((o) => {
    const key = o.orderId || o.$id;
    if (!byOrderId[key]) {
      byOrderId[key] = { customerId: o.customerId, total: 0, deliveryMethod: (o.deliveryMethod || 'unknown').trim() || 'unknown', status: (o.status || 'unknown').toLowerCase() };
    }
    byOrderId[key].total += parseFloat(o.totalAmount) || 0;
    const prodKey = o.productName || o.productId || 'unknown';
    if (prodKey !== 'unknown') {
      if (!byProduct[prodKey]) byProduct[prodKey] = { quantity: 0, revenue: 0, orderIds: new Set() };
      byProduct[prodKey].quantity += parseInt(o.quantity, 10) || 0;
      byProduct[prodKey].revenue += parseFloat(o.totalAmount) || 0;
      byProduct[prodKey].orderIds.add(key);
    }
  });

  Object.values(byOrderId).forEach(({ customerId, total, deliveryMethod, status }) => {
    if (customerId) {
      if (!byCustomer[customerId]) byCustomer[customerId] = { orderCount: 0, totalSpent: 0 };
      byCustomer[customerId].orderCount += 1;
      byCustomer[customerId].totalSpent += total;
    }
    if (!byDelivery[deliveryMethod]) byDelivery[deliveryMethod] = 0;
    byDelivery[deliveryMethod] += 1;
    if (!byStatus[status]) byStatus[status] = 0;
    byStatus[status] += 1;
  });

  const customerIds = Object.keys(byCustomer);
  const topCustomers = await Promise.all(
    customerIds.slice(0, 50).map(async (cid) => {
      try {
        const u = await tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, cid);
        const s = byCustomer[cid];
        return {
          customerId: cid,
          name: [u.fname, u.lname].filter(Boolean).join(' ') || u.email || cid,
          email: u.email,
          orderCount: s.orderCount,
          totalSpent: s.totalSpent,
        };
      } catch {
        const s = byCustomer[cid];
        return { customerId: cid, name: 'Unknown', email: null, orderCount: s.orderCount, totalSpent: s.totalSpent };
      }
    })
  );
  topCustomers.sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0));

  const topProductsSold = Object.entries(byProduct)
    .map(([name, d]) => ({ productName: name, quantity: d.quantity, revenue: d.revenue, orderCount: d.orderIds.size }))
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 15);

  const totalOrders = Object.keys(byOrderId).length;
  const totalRevenue = Object.values(byOrderId).reduce((s, o) => s + o.total, 0);
  const totalCustomers = customerIds.length;
  const totalProductTypes = Object.keys(byProduct).length;

  return {
    topCustomers,
    topProductsSold,
    deliveryUsage: byDelivery,
    orderStatusBreakdown: byStatus,
    totalOrders,
    totalRevenue,
    totalCustomers,
    totalProductTypes,
  };
}

export async function getStoreConversations(storeId, limit = 30) {
  const res = await tablesDB.listRows(DATABASE_ID, CONVERSATIONS_TABLE_ID, [
    Query.equal('storeId', storeId),
    Query.orderDesc('lastMessageAt'),
    Query.limit(limit),
  ]);
  return res.rows || [];
}

export async function getStoreActivity(storeId) {
  const [orders, requests, conversations] = await Promise.all([
    getStoreOrders(storeId, 20),
    getStoreDeliveryRequests(storeId, 20),
    getStoreConversations(storeId, 20),
  ]);

  const activity = [];
  orders.forEach((o) => {
    activity.push({
      type: 'order',
      id: o.$id,
      date: o.orderDate,
      title: `Order ${o.orderId || o.$id}`,
      status: o.status,
      meta: { totalAmount: o.totalAmount, customerId: o.customerId },
    });
  });
  requests.forEach((r) => {
    activity.push({
      type: 'request',
      id: r.$id,
      date: r.createdAt || r.$createdAt,
      title: `Delivery: ${r.packageName || 'Package'}`,
      status: r.status || (r.isComplete ? 'complete' : r.isTaken ? 'in_progress' : 'pending'),
      meta: { storeName: r.storeName, driverId: r.driverId },
    });
  });
  conversations.forEach((c) => {
    activity.push({
      type: 'conversation',
      id: c.$id,
      date: c.lastMessageAt || c.$createdAt,
      title: c.title || 'Conversation',
      meta: { lastMessage: c.lastMessageText },
    });
  });

  activity.sort((a, b) => {
    const da = new Date(a.date || 0).getTime();
    const db = new Date(b.date || 0).getTime();
    return db - da;
  });

  return activity.slice(0, 50);
}

export async function getStoreFullData(storeId) {
  const [
    store,
    products,
    orders,
    paymentMethods,
    deliveryRequests,
    conversations,
    activity,
    customers,
    storeAnalytics,
  ] = await Promise.all([
    getStoreOverview(storeId),
    getStoreProducts(storeId),
    getStoreOrders(storeId),
    getStorePaymentMethods(storeId),
    getStoreDeliveryRequests(storeId),
    getStoreConversations(storeId),
    getStoreActivity(storeId),
    getStoreCustomers(storeId),
    getStoreAnalyticsDetail(storeId),
  ]);

  return {
    store,
    products,
    orders,
    paymentMethods,
    deliveryRequests,
    conversations,
    activity,
    customers,
    storeAnalytics,
  };
}
